import { useEffect, useState } from 'react';
import type { Coordinate } from 'ol/coordinate';
import { toLonLat } from 'ol/proj';
import type { PhotoProperties } from '../../map/photoLayer';
import { fetchContentDownloadUrl } from '../../api/content';
import { DetailPanel } from '../DetailPanel/DetailPanel';
import { PhotoLayerIcon } from '../icons/Icons';
import './photo-detail-panel.css';

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes > 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes > 1_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${bytes} B`;
}

interface PhotoDetailPanelProps {
  properties: PhotoProperties;
  coordinate: Coordinate; // map-projection coordinate (EPSG:3857)
  onClose: () => void;
}

/**
 * Detail panel for a clicked field-photo pin — thin wrapper around the
 * shared DetailPanel chrome (see components/DetailPanel/), adding only what
 * a photo needs on top of it: a thumbnail (opens a fullscreen viewer) and
 * a "TEST FIXTURE" badge when the pin came from the committed dev fixture
 * rather than a real Ente export.
 *
 * Image URL resolution (v0.6.6): a real pin carries `content_item_id`, not a URL — this fetches
 * a short-lived signed URL from GET /api/v1/content/{id}/download on mount, the same endpoint
 * the Content page's Open/Download uses (ADR-0005), rather than eagerly signing every pin on the
 * map. The dev fixture's `image_url`/`thumbnail_url` (data: URIs, no signing needed) are used
 * as-is when present, so the fixture keeps working unchanged.
 */
export function PhotoDetailPanel({ properties, coordinate, onClose }: PhotoDetailPanelProps) {
  const [description, setDescription] = useState(properties.description ?? '');
  const [fullscreen, setFullscreen] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(properties.image_url);
  const [lon, lat] = toLonLat(coordinate);

  useEffect(() => {
    if (properties.image_url || !properties.content_item_id) return; // fixture path, or nothing to fetch
    let cancelled = false;
    fetchContentDownloadUrl(properties.content_item_id)
      .then((r) => {
        if (!cancelled) setResolvedUrl(r.url);
      })
      .catch((err: unknown) => {
        console.error('Failed to load field photo:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [properties.content_item_id, properties.image_url]);

  // Two-stage Escape: with the fullscreen viewer open, the first press
  // closes just the viewer and returns to this panel (which stays open);
  // only a second press (viewer already closed) closes the panel itself.
  // Plain bubble-phase listener, not capture — so it never preempts
  // MeasureToolbar's own capture-phase Escape-cancel handler when a
  // measure tool happens to be active at the same time (that one already
  // calls stopImmediatePropagation and should keep taking priority; this
  // listener then simply never fires for that keypress, same as before).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (fullscreen) {
        setFullscreen(false);
      } else {
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [fullscreen, onClose]);

  return (
    <>
      <DetailPanel
        icon={<PhotoLayerIcon size={15} />}
        title={properties.filename}
        onClose={onClose}
        badge={
          properties.test_fixture && (
            <div className="photo-panel-fixture-badge">TEST FIXTURE — not a real field photo</div>
          )
        }
        media={
          resolvedUrl && (
            <button
              type="button"
              className="photo-panel-thumb-btn"
              onClick={() => setFullscreen(true)}
              aria-label="Open fullscreen image"
            >
              <img className="photo-panel-thumb" src={resolvedUrl} alt={properties.filename} />
            </button>
          )
        }
        meta={
          <>
            {properties.uploader && <div>Captured by {properties.uploader}</div>}
            <div>{new Date(properties.capture_date).toLocaleString()}</div>
          </>
        }
        properties={[
          { label: 'File type', value: properties.file_type ?? '—' },
          { label: 'File size', value: formatBytes(properties.file_size_bytes) },
          { label: 'X (lon)', value: lon.toFixed(6) },
          { label: 'Y (lat)', value: lat.toFixed(6) },
          { label: 'Z', value: '—' },
        ]}
        description={description}
        onDescriptionChange={setDescription}
      />

      {fullscreen && resolvedUrl && (
        <div className="photo-fullscreen-overlay" role="dialog" aria-modal="true" onClick={() => setFullscreen(false)}>
          <button
            type="button"
            className="photo-fullscreen-close"
            onClick={() => setFullscreen(false)}
            aria-label="Close fullscreen image"
          >
            ×
          </button>
          <img
            className="photo-fullscreen-image"
            src={resolvedUrl}
            alt={properties.filename}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
